import api, { extractErrorMessage } from './api';

export type UgcFlowkitMode = 'faceless' | 'character';
export type UgcFlowkitLocationStrategy = 'generated-per-video' | 'folder';
export type UgcFlowkitScriptTone = 'natural' | 'fun' | 'story' | 'expert';
export interface UgcFlowkitLaunchConfig {
  winProducts: 'all' | number;
  bestProducts: number;
  goodProducts: number;
  selectedProductIds?: string[];
  durationSec: number;
  mode: UgcFlowkitMode;
  autoGenerate: boolean;
  autoMerge: boolean;
  productImageFolder: string;
  familyCharacterFolder: string;
  locationStrategy: UgcFlowkitLocationStrategy;
  locationFolder: string;
  promptOnly?: boolean;
  scriptTone?: UgcFlowkitScriptTone;
  channelHandle?: string;
  competitorHandles?: string[];
  selectedNiches?: string[];
}
export interface UgcFlowkitLaunchResult {
  batchId: string;
  status: 'queued' | 'running';
  manifestSha256: string;
  isQueued?: boolean;
  queuePosition?: number;
  products: { id: string; name: string; status: string }[];
}
export interface UgcFlowkitSelectionPreview {
  counts: { win: number; best: number; good: number; total: number };
  products: { id: string; name: string }[];
}
export type UgcFlowkitProductStatus = 'queued' | 'planning' | 'validated' | 'provisioned' | 'generating' | 'merged' | 'stopped_safe';
export type UgcReviewVerdict = 'pass' | 'acceptable' | 'fail';
export interface UgcFlowkitSceneReview {
  sceneNumber: number;
  verdict: UgcReviewVerdict;
  reason: string;
  correctiveLock?: string;
  transcript?: string;
  voScore?: number;
  soundScore?: number;
}
export interface UgcFlowkitReview {
  completedAt: string;
  usable: boolean;
  result?: UgcReviewVerdict;
  minimumScore?: number;
  findings: string[];
  qualityScores?: { hook: number; foodDesire: number; continuity: number };
  scenes?: UgcFlowkitSceneReview[];
}
export interface UgcFlowkitReviewRework {
  kind?: 'serious-fail' | 'quality-improvement';
  attempt: number;
  maxAttempts: number;
  sceneNumbers: number[];
  correctiveLocks: Array<{ sceneNumber: number; lock: string }>;
  stage: 'patching' | 'generating_images' | 'generating_videos' | 'reviewing';
  patchedAt?: string;
  imageRequestIds?: string[];
  videoRequestIds?: string[];
  completedAt?: string;
}
export interface UgcFlowkitReference { name: string; entityType: 'character' | 'location' | 'creature' | 'visual_asset'; mediaId: string; voiceDescription?: string }
export interface UgcFlowkitScene { number: number; sourceReferenceName: string; retainDurationSec: number; prompt: string; videoPrompt: string; voiceover: string; babyInterjection?: string }
export interface UgcFlowkitPlan { version: 1; hook: string; concept: string; voiceRole?: 'wife' | 'husband'; castRoles: ('wife' | 'husband' | 'baby')[]; location: UgcFlowkitReference; references: UgcFlowkitReference[]; scenes: UgcFlowkitScene[]; compliance: { evidenceUsed: string[]; claimsOmitted: string[]; commercialDisclosureRequired: true; aiGeneratedLabelRequired: true } }
export interface UgcFlowkitBatchSummary {
  id: string;
  createdAt: string;
  updatedAt: string;
  config: UgcFlowkitLaunchConfig;
  status: 'running' | 'queued' | 'completed' | 'stopped' | 'blocked';
  totalProducts: number;
  completedProducts: number;
  stoppedProducts: number;
  runningProducts: number;
  queuedProducts: number;
  blockedCode?: string;
  blockedMessage?: string;
  queuePosition?: number;
  productsSummary: Array<{ id: string; name: string; status: UgcFlowkitProductStatus }>;
}
export interface UgcFlowkitBatchesResponse {
  batches: UgcFlowkitBatchSummary[];
  queue: {
    activeBatchId: string | null;
    queuedBatchIds: string[];
  };
}
export interface UgcFlowkitBatch {
  version: 1;
  id: string;
  createdAt: string;
  sha256: string;
  isQueued?: boolean;
  queuePosition?: number;
  config: UgcFlowkitLaunchConfig & { plannerPolicy: 'codex-with-gemini-fallback' };
  runtime?: { blocked?: { code: string; message: string; at: string } };
  products: Array<{
    id: string;
    name: string;
    status: UgcFlowkitProductStatus;
    safeStop?: { code: string; message: string; at: string };
    runtime?: { phase: 'awaiting_bridge' | 'provisioning' | 'planning' | 'validated' | 'creating_scenes' | 'generating_images' | 'generating_videos' | 'reviewing' | 'reworking_review' | 'merging' | 'merged'; projectId?: string; videoId?: string; productMediaId?: string; productEntityId?: string; productUploadFile?: string; productGallery?: Array<{ filePath: string; fileName: string; entityId?: string; mediaId?: string }>; familyReferences?: Array<{ role: 'wife' | 'husband' | 'baby'; name: string; entityId: string; mediaId?: string; imageFile: string; voiceDescription: string }>; voiceProfiles?: Array<{ role: 'wife' | 'husband' | 'baby'; name: string; voiceDescription: string }>; galleryUploadIndex?: number; locationRequestId?: string; locationMediaId?: string; productVision?: { provider?: 'codex' | 'gemini-fallback'; kind: string; servingContext: string; locationPrompt: string; sourceImageCount: number; imageEvidence?: Array<{ sourceMediaId: string; fileName: string; visibleProductDetails: string; packagingAndLabelLock: string; insidePackagingProductLock: string; supportedVisualActions: string[]; forbiddenInferences: string[] }> }; planner?: 'codex' | 'gemini-fallback'; skillSha256?: string; foodKnowledge?: { source: string; reviewedAt: string; unitIds: string[]; atomIds: string[] }; scriptStudio?: { tone: UgcFlowkitScriptTone; channelHandle: string; competitorHandles: string[]; appliedAt: string }; plan?: UgcFlowkitPlan; planningIssue?: { code: string; message: string; at: string }; sceneIds?: string[]; imageRequestIds?: string[]; videoRequestIds?: string[]; review?: UgcFlowkitReview; reviewRework?: UgcFlowkitReviewRework; seriousReviewReworkAttempts?: number; qualityImprovementAttemptedAt?: string; merged?: { url: string; archivePath: string; clipCount: number; completedAt: string } };
  }>;
}

export type UgcFlowkitProduct = UgcFlowkitBatch['products'][number];

export const DEFAULT_UGC_FLOWKIT_CONFIG: UgcFlowkitLaunchConfig = {
  winProducts: 'all', bestProducts: 0, goodProducts: 0, durationSec: 20,
  mode: 'faceless', autoGenerate: true, autoMerge: true,
  productImageFolder: '/home/ai/homelab/DDlab.cloud/data/Products/Today_Win_Products',
  familyCharacterFolder: '/home/ai/homelab/DDlab.cloud/data/Characters/family',
  locationStrategy: 'generated-per-video', locationFolder: '',
  promptOnly: false,
  scriptTone: 'natural',
  channelHandle: '@dundimmean',
  competitorHandles: ['@taphoabeoo235', '@taphoacoc'],
  selectedNiches: ['snacks', 'candy', 'sauce', 'dried', 'farm'],
};

let explicitlySelectedBatchId: string | null = null;

export function getExplicitlySelectedBatchId(): string | null {
  return explicitlySelectedBatchId;
}

export function setExplicitlySelectedBatchId(batchId: string | null): void {
  explicitlySelectedBatchId = batchId;
}

function pipelinePayload(config: UgcFlowkitLaunchConfig) {
  const { familyCharacterFolder, locationFolder, ...base } = config;
  return {
    ...base,
    ...(familyCharacterFolder.trim() ? { familyCharacterFolder: familyCharacterFolder.trim() } : {}),
    ...(locationFolder.trim() ? { locationFolder: locationFolder.trim() } : {}),
  };
}

export async function previewUgcFlowkitPipeline(config: UgcFlowkitLaunchConfig): Promise<UgcFlowkitSelectionPreview> {
  try {
    return (await api.post<UgcFlowkitSelectionPreview>('/ugc-flowkit/pipeline/preview', pipelinePayload(config))).data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to preview the UGC Flowkit selection.'));
  }
}

export async function startUgcFlowkitPipeline(config: UgcFlowkitLaunchConfig): Promise<UgcFlowkitLaunchResult> {
  try {
    const response = await api.post<UgcFlowkitLaunchResult>('/ugc-flowkit/pipeline/start', pipelinePayload(config));
    return response.data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to start the UGC Flowkit pipeline.'));
  }
}

export async function getUgcFlowkitBatch(batchId: string): Promise<UgcFlowkitBatch> {
  try {
    return (await api.get<UgcFlowkitBatch>(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}`, { timeout: 15000 })).data;
  } catch (error) {
    const message = extractErrorMessage(error, 'Unable to load this UGC Flowkit batch.');
    if (/status code 404|ENOENT|not found/i.test(message)) {
      throw new Error(`Batch not found (404): ${message}`);
    }
    throw new Error(message);
  }
}

export async function getCurrentUgcFlowkitBatch(): Promise<UgcFlowkitBatch | null> {
  try {
    return (await api.get<UgcFlowkitBatch>('/ugc-flowkit/batch/current', { timeout: 15000 })).data;
  } catch (error) {
    const message = extractErrorMessage(error, 'Unable to find the current UGC Flowkit batch.');
    if (/no UGC Flowkit batch exists|status code 404|ENOENT|not found/i.test(message)) return null;
    throw new Error(message);
  }
}

export async function listUgcFlowkitBatches(): Promise<UgcFlowkitBatchesResponse> {
  try {
    return (await api.get<UgcFlowkitBatchesResponse>('/ugc-flowkit/batches', { timeout: 15000 })).data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to load UGC Flowkit batches.'));
  }
}

export async function stopUgcFlowkitBatch(batchId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/stop`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to stop this UGC Flowkit batch.'));
  }
}

export async function deleteUgcFlowkitBatch(batchId: string): Promise<void> {
  try {
    await api.delete(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to delete this UGC Flowkit batch.'));
  }
}

export async function cleanStaleFlowkitVideos(): Promise<{ deletedVideoIds: string[]; scannedVideoCount: number }> {
  try {
    return (await api.post<{ deletedVideoIds: string[]; scannedVideoCount: number }>('/ugc-flowkit/clean-stale')).data;
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to clean stale Flowkit videos.'));
  }
}

export async function dispatchUgcFlowkitBatch(batchId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/dispatch`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to dispatch this UGC Flowkit batch.'));
  }
}

/** Explicit operator action after the Flowkit browser bridge has been repaired or to resume stopped batch. */
export async function resumeUgcFlowkitBatch(batchId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/resume`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to resume this UGC Flowkit batch.'));
  }
}

/** Re-runs only deterministic schema-invalid plans, retaining existing Flowkit UUIDs. */
export async function retryInvalidUgcFlowkitPlans(batchId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/retry-invalid-plans`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to retry invalid UGC Flowkit plans.'));
  }
}

export async function retryUgcFlowkitProductPlan(batchId: string, productId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/products/${encodeURIComponent(productId)}/retry-invalid-plan`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to retry planning for this product.'));
  }
}

export async function stopUgcFlowkitProduct(batchId: string, productId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/products/${encodeURIComponent(productId)}/stop`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to stop this product pipeline.'));
  }
}

export async function resumeUgcFlowkitProduct(batchId: string, productId: string): Promise<void> {
  try {
    await api.post(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/products/${encodeURIComponent(productId)}/resume`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to resume this product pipeline.'));
  }
}

export async function deleteUgcFlowkitProduct(batchId: string, productId: string): Promise<void> {
  try {
    await api.delete(`/ugc-flowkit/batch/${encodeURIComponent(batchId)}/products/${encodeURIComponent(productId)}`);
  } catch (error) {
    throw new Error(extractErrorMessage(error, 'Unable to delete this product pipeline.'));
  }
}

export function formatGoogleFlowPrompts(product: {
  name: string;
  runtime?: {
    productVision?: { locationPrompt?: string };
    plan?: UgcFlowkitPlan;
    familyReferences?: Array<{ role: string; name: string; voiceDescription?: string }>;
  };
}): string {
  const { name, runtime } = product;
  const plan = runtime?.plan;
  const locationPrompt = runtime?.productVision?.locationPrompt || plan?.location?.name || '';

  const lines: string[] = [
    `=== GOOGLE FLOW PROMPT PACK: ${name} ===`,
    '',
    '--- 1. REFERENCE GENERATION ---',
    `Location Prompt:\n${locationPrompt}`,
  ];

  if (runtime?.familyReferences?.length) {
    lines.push('', 'Family Characters:');
    for (const ref of runtime.familyReferences) {
      lines.push(`- ${ref.role.toUpperCase()} (${ref.name}): ${ref.voiceDescription || ''}`);
    }
  }

  if (plan) {
    lines.push(
      '',
      `Hook: ${plan.hook}`,
      `Concept: ${plan.concept}`,
      `Voice Role: ${plan.voiceRole}`,
    );

    for (const scene of plan.scenes) {
      lines.push(
        '',
        `--- SCENE ${scene.number} (Duration: ${scene.retainDurationSec}s) ---`,
        `[Frame 0 Still Prompt]:\n${scene.prompt}`,
        '',
        `[8-Second Video Motion Prompt]:\n${scene.videoPrompt}`,
        '',
        `[Voiceover]:\n${scene.voiceover}${scene.babyInterjection ? ` (${scene.babyInterjection})` : ''}`,
      );
    }
  }

  return lines.join('\n');
}
